import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, Row, Col, Badge, Form, Button, Spinner, Dropdown, InputGroup } from 'react-bootstrap';
import { API } from 'aws-amplify';
import * as queries from '../../graphql/queries';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faCalendarAlt,
  faCheckCircle,
  faFileAlt,
  faObjectGroup,
  faInfoCircle,
  faArrowUp,
  faArrowDown,
} from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  extractEvents,
  filterEventsByRange,
  findClusters,
  filterClustersByRange,
  computeSummary,
  getDateRangePresets,
} from '../../utils/growthKpiUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const COLORS = {
  action: '#28a745',
  report: '#1a73e8',
  cluster: '#6f42c1',
  line: '#1a73e8',
  fill: 'rgba(26,115,232,0.06)',
  gridLine: '#f0f0f0',
  clusterBg: 'rgba(111,66,193,0.08)',
  clusterBorder: 'rgba(111,66,193,0.35)',
  // KPI line colours (mirror LeadershipFITT KPIView)
  kpiUp: '#4CAF50',   // higher is better
  kpiDown: '#F44336', // lower is better
  kpiTarget: '#FFA500',
};

// ── helpers ──

const fmtShort = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtLong = (d) =>
  d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

/**
 * Aggregate a sorted event list by dateKey.
 * Returns [ { dateKey, date, label, actions[], reports[], total } ]
 * Only dates with at least one event appear.
 */
const aggregateByDate = (events) => {
  const map = new Map();
  events.forEach((ev) => {
    if (!map.has(ev.dateKey)) {
      map.set(ev.dateKey, {
        dateKey: ev.dateKey,
        date: ev.date,
        label: fmtShort(ev.date),
        actions: [],
        reports: [],
      });
    }
    const bucket = map.get(ev.dateKey);
    if (ev.type === 'action') bucket.actions.push(ev);
    else bucket.reports.push(ev);
  });
  return Array.from(map.values())
    .map((b) => ({ ...b, total: b.actions.length + b.reports.length }))
    .sort((a, b) => a.date - b.date);
};

// ── Cluster band plugin ──

const makeClusterPlugin = (clusters, bucketDateKeys) => ({
  id: 'growthClusterBands',
  beforeDatasetsDraw(chart) {
    if (!clusters || clusters.length === 0) return;
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales.x) return;
    ctx.save();
    clusters.forEach((cluster) => {
      const startKey = cluster.events[0].dateKey;
      const endKey = cluster.events[cluster.events.length - 1].dateKey;
      const si = bucketDateKeys.indexOf(startKey);
      const ei = bucketDateKeys.indexOf(endKey);
      if (si < 0 || ei < 0) return;

      const pad = 18;
      const x1 = scales.x.getPixelForValue(si) - pad;
      const x2 = scales.x.getPixelForValue(ei) + pad;
      const y1 = chartArea.top;
      const h = chartArea.height;

      ctx.fillStyle = COLORS.clusterBg;
      ctx.beginPath();
      // roundRect may not be available on older Canvas, fallback to rect
      if (ctx.roundRect) {
        ctx.roundRect(x1, y1, x2 - x1, h, 6);
      } else {
        ctx.rect(x1, y1, x2 - x1, h);
      }
      ctx.fill();
      ctx.strokeStyle = COLORS.clusterBorder;
      ctx.lineWidth = 1;
      ctx.stroke();

      // label above band
      ctx.fillStyle = COLORS.cluster;
      ctx.font = 'bold 10px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(cluster.label, (x1 + x2) / 2, y1 - 3);
    });
    ctx.restore();
  },
});

// ── KPI marker plugin (⭐ target achieved, ▲/▼ off-scale) ──

const drawTriangleMarker = (ctx, cx, cy, size, color, direction) => {
  ctx.beginPath();
  if (direction === 'up') {
    ctx.moveTo(cx, cy - size);
    ctx.lineTo(cx + size, cy + size);
    ctx.lineTo(cx - size, cy + size);
  } else {
    ctx.moveTo(cx, cy + size);
    ctx.lineTo(cx + size, cy - size);
    ctx.lineTo(cx - size, cy - size);
  }
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
};

const makeKpiMarkerPlugin = (kpiLineDataRef, selectedKpiRef) => ({
  id: 'kpiMarkers',
  afterDatasetsDraw(chart) {
    const selectedKpi = selectedKpiRef.current;
    const kpiLineData = kpiLineDataRef.current;
    if (!selectedKpi || !kpiLineData || kpiLineData.length === 0) return;
    const { ctx, scales } = chart;
    if (!scales.y1) return;
    const target = Number(selectedKpi.target);
    if (isNaN(target)) return;
    const trend = selectedKpi.trend; // true = higher is better, false = lower is better

    ctx.save();
    kpiLineData.forEach((val, i) => {
      if (val === null || val === undefined) return;
      const x = scales.x.getPixelForValue(i);
      const y = scales.y1.getPixelForValue(val);

      const targetAchieved = trend ? val >= target : val <= target;

      // Off-scale: significantly beyond target in the bad/good direction
      const offScaleBad =
        target > 0
          ? trend
            ? val < target * 0.5  // trend=true: critically low
            : val > target * 3    // trend=false: critically high
          : false;

      const offScaleGood =
        target > 0
          ? trend
            ? val > target * 1.5  // trend=true: exceptionally high
            : val < target * 0.2  // trend=false: exceptionally low
          : false;

      if (targetAchieved) {
        ctx.font = '13px serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText('⭐', x, y - 4);
      }

      if (offScaleBad) {
        drawTriangleMarker(ctx, x, y + 10, 7, '#F44336', 'down');
      } else if (offScaleGood && !targetAchieved) {
        drawTriangleMarker(ctx, x, y - 10, 7, '#4CAF50', 'up');
      }
    });
    ctx.restore();
  },
});

// ── default range: last 4 months (fallback) ──
const defaultStart = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 4);
  d.setHours(0, 0, 0, 0);
  return d;
};

const toDateInputValue = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return dt.toISOString().split('T')[0];
};

/** Safely parse a date-like value into a local-midnight Date, or null. */
const parseProjectDate = (val) => {
  if (!val) return null;
  // Already a Date object
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  // Extract date portion only (handles 'YYYY-MM-DD', full ISO, etc.)
  const datePart = String(val).split('T')[0];
  const d = new Date(datePart + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const GrowthKpiRunChart = ({ projectId, projectStartDate, projectEndDate }) => {
  const chartRef = useRef(null);
  // Refs so the stable kpiMarkerPlugin always reads the latest values
  // (react-chartjs-2 v5 registers inline plugins once at creation time)
  const kpiLineDataRef = useRef([]);
  const selectedKpiRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [actionItems, setActionItems] = useState([]);
  const [reports, setReports] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');

  // ── User-defined KPIs (right axis) ──
  const [kpis, setKpis] = useState([]);
  const [selectedKpiId, setSelectedKpiId] = useState('');
  const [kpiPoints, setKpiPoints] = useState([]);
  const [kpiPointsLoading, setKpiPointsLoading] = useState(false);

  const selectedKpi = useMemo(
    () => kpis.find((k) => k.id === selectedKpiId) || null,
    [kpis, selectedKpiId]
  );

  // Initialise from project dates if available, otherwise last 4 months
  const initStart = parseProjectDate(projectStartDate) ?? defaultStart();
  const initEnd = parseProjectDate(projectEndDate) ?? new Date();

  const [customStart, setCustomStart] = useState(() => toDateInputValue(initStart));
  const [customEnd, setCustomEnd] = useState(() => toDateInputValue(initEnd));
  const [dateRange, setDateRange] = useState({ start: initStart, end: initEnd });

  // Sync date range when project props arrive (data loads asynchronously)
  useEffect(() => {
    if (projectStartDate) {
      const s = parseProjectDate(projectStartDate) ?? defaultStart();
      const e = parseProjectDate(projectEndDate) ?? new Date();
      setCustomStart(toDateInputValue(s));
      setCustomEnd(toDateInputValue(e));
      setDateRange({ start: s, end: e });
    }
  }, [projectStartDate, projectEndDate]);

  // ── fetch ──
  useEffect(() => {
    if (projectId) fetchData();
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch action items and reports in parallel; KPIs in a separate try so a
      // schema mismatch on the KPI query never silently kills the whole chart.
      const [aiRes, repRes] = await Promise.all([
        API.graphql({
          query: queries.actionItemsByProjectID,
          variables: { projectID: projectId, limit: 1000 },
        }),
        API.graphql({
          query: queries.reportsByProjectID,
          variables: { projectID: projectId, limit: 1000 },
        }),
      ]);
      setActionItems(aiRes.data.actionItemsByProjectID.items);
      setReports(repRes.data.reportsByProjectID.items);
    } catch (err) {
      console.error('GrowthKPI events fetch error:', err);
    } finally {
      setLoading(false);
    }

    // KPI list loaded separately — failure here won't break the chart
    try {
      const kpiRes = await API.graphql({
        query: queries.kPISByProjectID,
        variables: { projectID: projectId, limit: 1000 },
      });
      const liveKpis = (kpiRes.data.kPISByProjectID.items || []).filter((k) => !k._deleted);
      setKpis(liveKpis);
    } catch (err) {
      console.error('GrowthKPI KPI list fetch error:', err);
    }
  };

  // ── Fetch KPI data points when a KPI is selected ──
  useEffect(() => {
    if (!selectedKpiId) {
      setKpiPoints([]);
      return;
    }
    let cancelled = false;
    const loadKpiPoints = async () => {
      setKpiPointsLoading(true);
      try {
        const res = await API.graphql({
          query: queries.kPIDataByKpiID,
          variables: { kpiID: selectedKpiId, limit: 1000 },
        });
        const items = (res.data.kPIDataByKpiID.items || [])
          .filter((d) => !d._deleted && d.yAxisvalue !== null && d.yAxisvalue !== undefined)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        if (!cancelled) setKpiPoints(items);
      } catch (err) {
        console.error('KPI data fetch error:', err);
        if (!cancelled) setKpiPoints([]);
      } finally {
        if (!cancelled) setKpiPointsLoading(false);
      }
    };
    loadKpiPoints();
    return () => {
      cancelled = true;
    };
  }, [selectedKpiId]);

  // ── derived data ──
  const allEvents = useMemo(() => extractEvents(actionItems, reports), [actionItems, reports]);
  const filteredEvents = useMemo(
    () => filterEventsByRange(allEvents, dateRange.start, dateRange.end),
    [allEvents, dateRange]
  );
  // Cluster on the FULL event history, not the range-filtered one — otherwise a
  // cluster whose earlier events fall just outside the selected range drops
  // below the 2-event threshold and silently disappears. Range selection only
  // filters which already-detected clusters are shown.
  const { clusters: allClusters } = useMemo(() => findClusters(allEvents), [allEvents]);
  const clusters = useMemo(
    () => filterClustersByRange(allClusters, dateRange.start, dateRange.end),
    [allClusters, dateRange]
  );
  const summary = useMemo(() => computeSummary(filteredEvents, clusters), [filteredEvents, clusters]);

  // One bucket per unique event-date (only dates with events)
  const buckets = useMemo(() => aggregateByDate(filteredEvents), [filteredEvents]);
  const bucketDateKeys = useMemo(() => buckets.map((b) => b.dateKey), [buckets]);

  // ── date handlers ──
  const applyPreset = useCallback((name) => {
    const presets = getDateRangePresets();
    if (!presets[name]) return;
    const { start, end } = presets[name];
    setSelectedPreset(name);
    setDateRange({ start, end });
    setCustomStart(start.toISOString().split('T')[0]);
    setCustomEnd(end.toISOString().split('T')[0]);
  }, []);

  const applyCustom = useCallback(() => {
    if (!customStart || !customEnd) return;
    // Use T00:00:00 so JS parses in local time, not UTC
    setSelectedPreset('Custom');
    setDateRange({
      start: new Date(customStart + 'T00:00:00'),
      end: new Date(customEnd + 'T23:59:59'),
    });
  }, [customStart, customEnd]);

  // ── chart datasets ──

  // Filter selected KPI's data points to current date range
  const filteredKpiPoints = useMemo(() => {
    if (!selectedKpi || kpiPoints.length === 0) return [];
    const s = new Date(dateRange.start);
    s.setHours(0, 0, 0, 0);
    const e = new Date(dateRange.end);
    e.setHours(23, 59, 59, 999);
    return kpiPoints
      .map((p) => ({ ...p, _date: new Date(p.date) }))
      .filter((p) => p._date >= s && p._date <= e)
      .sort((a, b) => a._date - b._date);
  }, [selectedKpi, kpiPoints, dateRange]);

  // Build a unified, sorted list of dateKeys spanning growth buckets + KPI points
  const toKey = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  const unifiedDates = useMemo(() => {
    const map = new Map(); // dateKey -> Date
    buckets.forEach((b) => {
      if (!map.has(b.dateKey)) map.set(b.dateKey, b.date);
    });
    filteredKpiPoints.forEach((p) => {
      const k = toKey(p._date);
      if (!map.has(k)) map.set(k, p._date);
    });
    return Array.from(map.entries())
      .map(([dateKey, date]) => ({ dateKey, date }))
      .sort((a, b) => a.date - b.date);
  }, [buckets, filteredKpiPoints]);

  const unifiedDateKeys = useMemo(() => unifiedDates.map((u) => u.dateKey), [unifiedDates]);
  const labels = useMemo(() => unifiedDates.map((u) => fmtShort(u.date)), [unifiedDates]);

  // Bucket by dateKey for fast lookup
  const bucketByKey = useMemo(() => {
    const m = {};
    buckets.forEach((b) => {
      m[b.dateKey] = b;
    });
    return m;
  }, [buckets]);

  // Growth completion data aligned to the unified labels (null on KPI-only dates)
  const growthLineData = useMemo(
    () => unifiedDateKeys.map((k) => (bucketByKey[k] ? bucketByKey[k].total : null)),
    [unifiedDateKeys, bucketByKey]
  );

  // Per-point colour & radius — only meaningful at indices that have a bucket
  const pointColors = useMemo(
    () =>
      unifiedDateKeys.map((k) => {
        const b = bucketByKey[k];
        if (!b) return COLORS.line;
        if (b.actions.length > 0 && b.reports.length > 0) return COLORS.cluster;
        if (b.reports.length > 0) return COLORS.report;
        return COLORS.action;
      }),
    [unifiedDateKeys, bucketByKey]
  );

  const pointRadii = useMemo(
    () =>
      unifiedDateKeys.map((k) => {
        const b = bucketByKey[k];
        return b ? Math.min(4 + b.total * 2, 12) : 0;
      }),
    [unifiedDateKeys, bucketByKey]
  );

  // Cluster marker dataset: positioned at last-event date of each cluster
  const clusterDataset = useMemo(
    () =>
      clusters
        .map((c) => {
          const idx = unifiedDateKeys.indexOf(c.markerDateKey);
          return { idx, cluster: c };
        })
        .filter((c) => c.idx >= 0),
    [clusters, unifiedDateKeys]
  );

  const clusterMarkerData = useMemo(() => {
    const data = new Array(unifiedDateKeys.length).fill(null);
    clusterDataset.forEach(({ idx }) => {
      const lineVal = bucketByKey[unifiedDateKeys[idx]]?.total || 0;
      data[idx] = lineVal + 0.6;
    });
    return data;
  }, [clusterDataset, unifiedDateKeys, bucketByKey]);

  const chartPlugin = useMemo(
    () => makeClusterPlugin(clusters, unifiedDateKeys),
    [clusters, unifiedDateKeys]
  );

  // ── KPI (right-axis) datasets ──
  const kpiPointByKey = useMemo(() => {
    const m = {};
    filteredKpiPoints.forEach((p) => {
      m[toKey(p._date)] = p;
    });
    return m;
  }, [filteredKpiPoints]);

  const kpiLineData = useMemo(
    () =>
      unifiedDateKeys.map((k) => {
        const p = kpiPointByKey[k];
        return p ? Number(p.yAxisvalue) : null;
      }),
    [unifiedDateKeys, kpiPointByKey]
  );

  const kpiColor = selectedKpi
    ? selectedKpi.trend
      ? COLORS.kpiUp
      : COLORS.kpiDown
    : COLORS.kpiUp;

  const kpiTargetData = useMemo(() => {
    if (!selectedKpi || selectedKpi.target === null || selectedKpi.target === undefined) return [];
    return unifiedDateKeys.map(() => Number(selectedKpi.target));
  }, [selectedKpi, unifiedDateKeys]);

  // Keep refs current whenever the derived values change
  useEffect(() => { kpiLineDataRef.current = kpiLineData; }, [kpiLineData]);
  useEffect(() => { selectedKpiRef.current = selectedKpi; }, [selectedKpi]);

  // Plugin is created once; it reads from refs so it never uses stale data
  const kpiMarkerPlugin = useMemo(
    () => makeKpiMarkerPlugin(kpiLineDataRef, selectedKpiRef),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const chartData = useMemo(() => {
    const datasets = [
      {
        label: 'Completions',
        data: growthLineData,
        borderColor: COLORS.line,
        backgroundColor: COLORS.fill,
        fill: true,
        tension: 0.25,
        spanGaps: true,
        pointBackgroundColor: pointColors,
        pointBorderColor: pointColors.map(() => '#fff'),
        pointBorderWidth: 2,
        pointRadius: pointRadii,
        pointHoverRadius: pointRadii.map((r) => r + 3),
        borderWidth: 2.5,
        order: 3,
        yAxisID: 'y',
      },
      {
        label: 'Clusters',
        data: clusterMarkerData,
        borderColor: COLORS.cluster,
        backgroundColor: COLORS.cluster,
        pointStyle: 'rectRot',
        pointRadius: clusterMarkerData.map((v) => (v !== null ? 10 : 0)),
        pointHoverRadius: clusterMarkerData.map((v) => (v !== null ? 13 : 0)),
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        showLine: false,
        order: 2,
        yAxisID: 'y',
      },
    ];

    if (selectedKpi) {
      datasets.push({
        label: selectedKpi.title || 'KPI',
        data: kpiLineData,
        borderColor: kpiColor,
        backgroundColor: kpiColor,
        spanGaps: true,
        tension: 0.2,
        pointRadius: kpiLineData.map((v) => (v !== null ? 5 : 0)),
        pointHoverRadius: kpiLineData.map((v) => (v !== null ? 7 : 0)),
        pointBackgroundColor: kpiColor,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        borderWidth: 2.5,
        fill: false,
        order: 1,
        yAxisID: 'y1',
      });

      if (kpiTargetData.length > 0) {
        datasets.push({
          label: `Target (${selectedKpi.target})`,
          data: kpiTargetData,
          borderColor: COLORS.kpiTarget,
          backgroundColor: COLORS.kpiTarget,
          borderDash: [6, 4],
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          order: 0,
          yAxisID: 'y1',
        });
      }
    }

    return { labels, datasets };
  }, [
    labels,
    growthLineData,
    pointColors,
    pointRadii,
    clusterMarkerData,
    selectedKpi,
    kpiLineData,
    kpiColor,
    kpiTargetData,
  ]);

  // Lookup: unified-index -> cluster (for tooltip)
  const clusterByIndex = useMemo(() => {
    const map = {};
    clusterDataset.forEach(({ idx, cluster }) => {
      map[idx] = cluster;
    });
    return map;
  }, [clusterDataset]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: !!selectedKpi, position: 'bottom', labels: { font: { size: 11 } } },
        tooltip: {
          backgroundColor: '#fff',
          titleColor: '#222',
          bodyColor: '#555',
          borderColor: '#dee2e6',
          borderWidth: 1,
          padding: 12,
          titleFont: { weight: 'bold', size: 13 },
          bodyFont: { size: 12 },
          callbacks: {
            title: (items) => {
              const idx = items[0]?.dataIndex;
              if (idx === undefined || !unifiedDates[idx]) return '';
              return fmtLong(unifiedDates[idx].date);
            },
            label: (item) => {
              const ds = item.dataset || {};
              // Clusters dataset
              if (ds.label === 'Clusters') {
                const c = clusterByIndex[item.dataIndex];
                if (!c) return null;
                return '\u2B25 Cluster: ' + c.label;
              }
              // KPI line / target
              if (ds.yAxisID === 'y1') {
                if (item.raw === null || item.raw === undefined) return null;
                const unit = selectedKpi?.yAxisLabel ? ` ${selectedKpi.yAxisLabel}` : '';
                if (ds.label?.startsWith('Target')) {
                  return `Target: ${item.raw}${unit}`;
                }
                return `${selectedKpi?.title || 'KPI'}: ${item.raw}${unit}`;
              }
              // Completions line
              const k = unifiedDateKeys[item.dataIndex];
              const b = bucketByKey[k];
              if (!b) return null;
              const lines = [];
              if (b.actions.length > 0) {
                lines.push('\u2713 ' + b.actions.length + ' Action Item' + (b.actions.length !== 1 ? 's' : ''));
                b.actions.forEach((a) => lines.push('    ' + a.title));
              }
              if (b.reports.length > 0) {
                lines.push('\u25C8 ' + b.reports.length + ' Report' + (b.reports.length !== 1 ? 's' : ''));
                b.reports.forEach((r) => lines.push('    ' + r.title));
              }
              return lines;
            },
            labelColor: (item) => {
              const ds = item.dataset || {};
              if (ds.label === 'Clusters') {
                return { borderColor: COLORS.cluster, backgroundColor: COLORS.cluster };
              }
              if (ds.yAxisID === 'y1') {
                const c = ds.borderColor || kpiColor;
                return { borderColor: c, backgroundColor: c };
              }
              const c = pointColors[item.dataIndex] || COLORS.line;
              return { borderColor: c, backgroundColor: c };
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          grid: { display: false },
          ticks: {
            color: '#6c757d',
            font: { size: 11 },
            maxRotation: 45,
            minRotation: 30,
          },
        },
        y: {
          display: true,
          position: 'left',
          beginAtZero: true,
          grid: { color: COLORS.gridLine },
          ticks: { stepSize: 1, precision: 0, color: '#6c757d', font: { size: 11 } },
          title: {
            display: true,
            text: 'Completions (Growth)',
            color: '#6c757d',
            font: { size: 12 },
          },
        },
        ...(selectedKpi
          ? {
              y1: {
                display: true,
                position: 'right',
                // Use stored min/max when available; fall back to auto-scale for legacy KPIs
                ...(selectedKpi.minValue != null && selectedKpi.maxValue != null
                  ? { min: Number(selectedKpi.minValue), max: Number(selectedKpi.maxValue) }
                  : { beginAtZero: true }),
                grid: { drawOnChartArea: false },
                ticks: { color: kpiColor, font: { size: 11 } },
                title: {
                  display: true,
                  text:
                    `${selectedKpi.title || 'KPI'}` +
                    (selectedKpi.yAxisLabel ? ` (${selectedKpi.yAxisLabel})` : ''),
                  color: kpiColor,
                  font: { size: 12 },
                },
              },
            }
          : {}),
      },
    }),
    [
      unifiedDates,
      unifiedDateKeys,
      bucketByKey,
      pointColors,
      clusterByIndex,
      selectedKpi,
      kpiColor,
    ]
  );

  // ── render ──

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Loading Growth KPI...</p>
      </div>
    );
  }

  const hasEvents = buckets.length > 0;
  const hasKpiPoints = filteredKpiPoints.length > 0;
  const hasAnyData = unifiedDates.length > 0;

  return (
    <>
      {/* Filters */}
      <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
        <FontAwesomeIcon icon={faCalendarAlt} className="text-muted flex-shrink-0" />
        <InputGroup size="sm" style={{ width: 'auto', flexWrap: 'nowrap' }}>
          <Form.Control
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            style={{ width: 135, minWidth: 135 }}
          />
          <InputGroup.Text className="px-2">&ndash;</InputGroup.Text>
          <Form.Control
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            style={{ width: 135, minWidth: 135 }}
          />
          <Button variant="outline-primary" onClick={applyCustom} style={{ whiteSpace: 'nowrap' }}>
            Apply
          </Button>
        </InputGroup>
        <Dropdown>
          <Dropdown.Toggle variant="outline-secondary" size="sm">
            {selectedPreset || 'Preset'}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {projectStartDate && (
              <Dropdown.Item
                active={selectedPreset === 'Project Range'}
                onClick={() => {
                  const s = new Date(projectStartDate);
                  const e = projectEndDate ? new Date(projectEndDate) : new Date();
                  setSelectedPreset('Project Range');
                  setDateRange({ start: s, end: e });
                  setCustomStart(toDateInputValue(s));
                  setCustomEnd(toDateInputValue(e));
                }}
              >
                Project Range
              </Dropdown.Item>
            )}
            {Object.keys(getDateRangePresets()).map((p) => (
              <Dropdown.Item key={p} active={selectedPreset === p} onClick={() => applyPreset(p)}>
                {p}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        {/* KPI overlay selector */}
        <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
          <Form.Label className="mb-0 text-muted small">Overlay KPI:</Form.Label>
          <Form.Select
            size="sm"
            value={selectedKpiId}
            onChange={(e) => setSelectedKpiId(e.target.value)}
            style={{ width: 'auto', minWidth: 200 }}
          >
            <option value="">— None —</option>
            {kpis.map((k) => (
              <option key={k.id} value={k.id}>
                {k.title} {k.trend ? '↑' : '↓'}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      <Row>
        {/* Chart */}
        <Col lg={9}>
          {/* Understanding This Chart — shown when a KPI is overlaid */}
          {selectedKpi && (
            <div
              className="mb-3 py-2 px-3"
              style={{
                backgroundColor: '#e8f4fd',
                border: '1px solid #bee3f8',
                borderRadius: 8,
                fontSize: '0.8rem',
              }}
            >
              <div className="d-flex gap-2 align-items-start">
                <FontAwesomeIcon icon={faInfoCircle} className="text-info flex-shrink-0 mt-1" />
                <div>
                  <strong>Understanding This Chart</strong>
                  <div className="mt-1">
                    <strong>Left Axis (Blue):</strong> Growth completions (action items &amp; reports) showing team activity.
                  </div>
                  <div>
                    <strong>Right Axis ({selectedKpi.trend ? 'Green' : 'Red'}):</strong>{' '}
                    {selectedKpi.title} raw values
                    {selectedKpi.yAxisLabel ? ` (${selectedKpi.yAxisLabel})` : ''}. The dashed line shows the target value.
                  </div>
                  <ul className="mb-0 mt-1" style={{ paddingLeft: '1.2rem' }}>
                    <li>
                      ⭐ = Value meets or {selectedKpi.trend ? 'exceeds' : 'goes below'} target
                      {selectedKpi.target != null ? ` (${selectedKpi.target}${selectedKpi.yAxisLabel ? ` ${selectedKpi.yAxisLabel}` : ''})` : ''}
                    </li>
                    <li>Dashed line = Target value for the KPI</li>
                    <li>▲ Off-scale = Exceptional (beyond range in good direction)</li>
                    <li>▼ Off-scale = Critical (beyond range in bad direction)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
            ⓘ Only <strong>completed reports</strong> and action items marked <strong>Done</strong> are plotted; in-progress items are not counted.
          </div>

          {hasAnyData ? (
            <div style={{ height: 380, position: 'relative' }}>
              <Line ref={chartRef} data={chartData} options={chartOptions} plugins={[chartPlugin, kpiMarkerPlugin]} />
            </div>
          ) : (
            <div
              className="d-flex flex-column align-items-center justify-content-center text-muted"
              style={{
                height: 300,
                background: '#fafbfc',
                borderRadius: 8,
                border: '1px dashed #dee2e6',
              }}
            >
              <FontAwesomeIcon icon={faChartLine} size="2x" className="mb-2" />
              <p className="mb-1">No completed items in the selected date range.</p>
              <small>
                This chart only counts <strong>completed reports</strong> and action items
                marked <strong>Done</strong> — reports/action items that are still in progress
                don&apos;t appear here. Mark items complete (or select a KPI overlay) to see data.
              </small>
            </div>
          )}

          {/* Selected KPI summary (mirrors LeadershipFITT KPIView indicator + colors) */}
          {selectedKpi && (
            <div className="mt-3">
              <Card className="border" style={{ borderColor: kpiColor + '55' }}>
                <Card.Body className="py-2 px-3">
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div className="d-flex align-items-center gap-2">
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 3,
                          backgroundColor: kpiColor,
                          display: 'inline-block',
                        }}
                      />
                      <span className="fw-semibold">{selectedKpi.title}</span>
                    </div>
                    <Badge
                      bg={selectedKpi.trend ? 'success' : 'danger'}
                      className="d-inline-flex align-items-center gap-1"
                    >
                      <FontAwesomeIcon
                        icon={selectedKpi.trend ? faArrowUp : faArrowDown}
                        style={{ fontSize: '0.65rem' }}
                      />
                      {selectedKpi.trend ? 'Higher is better' : 'Lower is better'}
                    </Badge>
                    {selectedKpi.target !== null && selectedKpi.target !== undefined && (
                      <small className="text-muted">
                        Target:&nbsp;
                        <span className="fw-semibold" style={{ color: COLORS.kpiTarget }}>
                          {selectedKpi.target}
                          {selectedKpi.yAxisLabel ? ` ${selectedKpi.yAxisLabel}` : ''}
                        </span>
                      </small>
                    )}
                    <small className="text-muted ms-auto">
                      {kpiPointsLoading
                        ? 'Loading KPI data…'
                        : `${filteredKpiPoints.length} data point${filteredKpiPoints.length === 1 ? '' : 's'} in range`}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}

          {/* Explanation row */}
          <Row className="mt-4 g-3">
            <Col md={4}>
              <Card className="h-100 border bg-light">
                <Card.Body className="py-3">
                  <h6
                    className="fw-bold text-uppercase mb-2"
                    style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}
                  >
                    Individual Events
                  </h6>
                  <p className="text-muted mb-2" style={{ fontSize: '0.78rem' }}>
                    Single completions appear as individual markers on the chart.
                  </p>
                  <div className="d-flex gap-3 flex-wrap">
                    <LegendDot color={COLORS.action} label="Action Item" />
                    <LegendDot color={COLORS.report} label="Report" />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 border bg-light">
                <Card.Body className="py-3">
                  <h6
                    className="fw-bold text-uppercase mb-2"
                    style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}
                  >
                    Clusters
                  </h6>
                  <p className="text-muted mb-2" style={{ fontSize: '0.78rem' }}>
                    Multiple completions within 3 days are grouped into a cluster.
                  </p>
                  <div className="d-flex align-items-center gap-2">
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        backgroundColor: COLORS.cluster,
                        transform: 'rotate(45deg)',
                        display: 'inline-block',
                        borderRadius: 2,
                      }}
                    />
                    <small className="text-muted">Cluster Marker &mdash; positioned at last event.</small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 border bg-light">
                <Card.Body className="py-3">
                  <h6
                    className="fw-bold text-uppercase mb-2"
                    style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}
                  >
                    Impact on KPI
                  </h6>
                  <p className="text-muted" style={{ fontSize: '0.78rem' }}>
                    Clusters show coordinated improvement efforts. Larger dots = more
                    completions on that day.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>

        {/* Right sidebar */}
        <Col lg={3}>
          {/* Legend */}
          <Card className="mb-3" style={{ backgroundColor: '#f0f4f8', border: 'none' }}>
            <Card.Body className="py-3 px-3">
              <h6
                className="fw-bold mb-3 text-uppercase"
                style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}
              >
                Event Legend
              </h6>
              <div className="d-flex flex-column gap-3">
                <LegendRow
                  icon={faCheckCircle}
                  bgColor={COLORS.action}
                  title="Action Item Completed"
                  subtitle="Individual task or action"
                />
                <LegendRow
                  icon={faFileAlt}
                  bgColor={COLORS.report}
                  title="Report Completed"
                  subtitle="Project-level analysis/report"
                  shape="rounded"
                />
                <LegendRow
                  icon={faObjectGroup}
                  bgColor={COLORS.cluster}
                  title="Cluster"
                  subtitle="Multiple completions in a short window"
                />
              </div>

              {/* KPI-specific legend entries */}
              {selectedKpi && (
                <>
                  <hr className="my-2" />
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex align-items-start gap-2">
                      <span style={{ fontSize: '1rem', lineHeight: 1, marginTop: 2 }}>⭐</span>
                      <div>
                        <div className="fw-semibold" style={{ fontSize: '0.82rem' }}>Target Achieved</div>
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                          ☆ = Value meets or {selectedKpi.trend ? 'exceeds' : 'goes below'} target
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-start gap-2">
                      <span style={{ color: '#F44336', fontSize: '0.9rem', lineHeight: 1, marginTop: 2 }}>▼</span>
                      <div>
                        <div className="fw-semibold" style={{ fontSize: '0.82rem' }}>Off-Scale Critical</div>
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                          Value beyond range in bad direction
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-start gap-2">
                      <span style={{ color: '#4CAF50', fontSize: '0.9rem', lineHeight: 1, marginTop: 2 }}>▲</span>
                      <div>
                        <div className="fw-semibold" style={{ fontSize: '0.82rem' }}>Off-Scale Exceptional</div>
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                          Value beyond range in good direction
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>

          {/* Events List */}
          <Card style={{ backgroundColor: '#fffbea', border: 'none' }}>
            <Card.Body className="py-3 px-3">
              <h6
                className="fw-bold mb-3 text-uppercase"
                style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}
              >
                Events List (This Period)
              </h6>
              <div className="d-flex flex-column gap-2">
                <SummaryRow
                  icon={faCheckCircle}
                  color={COLORS.action}
                  label="Action Items"
                  count={summary.actionItems}
                />
                <SummaryRow
                  icon={faFileAlt}
                  color={COLORS.report}
                  label="Reports"
                  count={summary.reports}
                  shape="rounded"
                />
                <SummaryRow
                  icon={faObjectGroup}
                  color={COLORS.cluster}
                  label="Clusters"
                  count={summary.clusters}
                />
              </div>
              <div className="mt-3 pt-2 border-top">
                <div className="d-flex align-items-start gap-1">
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="text-primary mt-1"
                    style={{ fontSize: '0.7rem' }}
                  />
                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                    Events are added automatically when items are marked complete. Clusters
                    highlight coordinated improvement efforts.
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Cluster details */}
          {clusters.length > 0 && (
            <div className="mt-3">
              <h6
                className="text-muted mb-2"
                style={{
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Cluster Detail
              </h6>
              {clusters.map((c, i) => (
                <Card
                  key={i}
                  className="mb-2"
                  style={{ backgroundColor: '#f3eeff', border: '1px solid #d4bbff' }}
                >
                  <Card.Body className="py-2 px-3">
                    <div
                      className="fw-semibold"
                      style={{ fontSize: '0.8rem', color: COLORS.cluster }}
                    >
                      {fmtShort(c.startDate)} &ndash; {fmtShort(c.endDate)}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                      {c.label}
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};

// ── tiny sub-components ──

const LegendDot = ({ color, label }) => (
  <div className="d-flex align-items-center gap-1">
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: color,
        display: 'inline-block',
      }}
    />
    <small className="text-muted">{label}</small>
  </div>
);

const LegendRow = ({ icon, bgColor, title, subtitle, shape }) => (
  <div className="d-flex align-items-start gap-2">
    <span
      className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 mt-1 ${
        shape === 'rounded' ? 'rounded' : 'rounded-circle'
      }`}
      style={{ width: 24, height: 24, backgroundColor: bgColor }}
    >
      <FontAwesomeIcon icon={icon} className="text-white" style={{ fontSize: '0.65rem' }} />
    </span>
    <div>
      <div className="fw-semibold" style={{ fontSize: '0.82rem' }}>
        {title}
      </div>
      <div className="text-muted" style={{ fontSize: '0.72rem' }}>
        {subtitle}
      </div>
    </div>
  </div>
);

const SummaryRow = ({ icon, color, label, count, shape }) => (
  <div className="d-flex justify-content-between align-items-center">
    <div className="d-flex align-items-center gap-2">
      <span
        className={`d-inline-flex align-items-center justify-content-center ${
          shape === 'rounded' ? 'rounded' : 'rounded-circle'
        }`}
        style={{ width: 20, height: 20, backgroundColor: color }}
      >
        <FontAwesomeIcon icon={icon} className="text-white" style={{ fontSize: '0.5rem' }} />
      </span>
      <span style={{ fontSize: '0.85rem' }}>{label}</span>
    </div>
    <Badge bg="dark" pill>
      {count}
    </Badge>
  </div>
);

export default GrowthKpiRunChart;
